import {ContentContainer} from '@coolcolduk/ui-mui';
import {Box, Paper, Typography, useMediaQuery, useTheme} from '@mui/material';
import {useState} from 'react';
import {useParams} from 'react-router';
import {UserPermission} from '@cp/model';
import BondTextAttribution from '../../bonds/bondTextAttribution/BondTextAttribution';
import ErrorComponent from '../../components/ErrorComponent/ErrorComponent';
import {env} from '../../config/env';
import {PrivateRouteProps} from '../../config/routeType';
import LoadingPage from '../other/LoadingPage';
import StudyPageAdmin from './StudyPageAdmin';
import StudyPageContent from './StudyPageContent';
import StudyPageHeader from './StudyPageHeader';
import StudyPageSectionContent from './StudyPageSectionContent';
import StudyPageSectionNavigation from './StudyPageSectionNavigation';
import StudyPageTableOfContents from './StudyPageTableOfContents';
import StudyPageTableOfContentsDrawer from './StudyPageTableOfContentsDrawer';
import useStudyPage from './useStudyPage';

export default function StudyPage({user}: PrivateRouteProps) {
  const {topic} = useParams<{topic: string}>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTouchDevice = useMediaQuery(theme.breakpoints.down('lg')); // Includes mobile and tablets

  const {
    examModule,
    study,
    sections,
    toc,
    currentSection,
    currentSectionIndex,
    currentStudyProgressDone,
    subjectColor,
    moduleLoading,
    studyLoading,
    statusUpdating,
    progressUpdating,
    moduleError,
    studyError,
    handleNext,
    handlePrevious,
    handleGoto,
    handleStatusChange,
    handleMarkAsDone,
    handleBack,
    handleEdit,
  } = useStudyPage();

  const [tocOpen, setTocOpen] = useState(!isMobile);

  // Swipe handling for mobile and tablets
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > env.general.swipe.minDistance;
    const isRightSwipe = distance < -env.general.swipe.minDistance;

    if (isLeftSwipe) {
      handleNext();
    }
    if (isRightSwipe) {
      handlePrevious();
    }
  };

  const isAdmin = Boolean(user?.roles?.includes(UserPermission.ADMIN));

  const handleSectionChangeWithToc = (index: number) => {
    handleGoto(index);
    if (isMobile) {
      setTocOpen(false);
    }
  };

  if (moduleLoading || studyLoading) {
    return <LoadingPage center minusAppBar />;
  }

  if (moduleError || studyError) {
    return <ErrorComponent message="Failed to load study content" buttonText="Go back" handleClick={handleBack} />;
  }

  if (!study) {
    return <ErrorComponent message="Study content not found" buttonText="Go back" handleClick={handleBack} />;
  }

  return (
    <ContentContainer data-testid="study-page">
      <StudyPageHeader onBack={handleBack} onToggleToc={() => setTocOpen(true)} showTocButton={isMobile} />

      <StudyPageAdmin
        isAdmin={isAdmin}
        showAdminControls={!!(study && examModule)}
        studyStatus={study?.status || undefined}
        onStatusChange={handleStatusChange}
        statusUpdating={statusUpdating}
        onEdit={handleEdit}
        onMarkAsDone={handleMarkAsDone}
        currentStudyProgressDone={currentStudyProgressDone}
        progressUpdating={progressUpdating}
      />

      <BondTextAttribution examModule={examModule} />

      <StudyPageContent topic={topic || ''} examModule={examModule || undefined} subjectColor={subjectColor}>
        {sections.length > 0 ? (
          <Box sx={{display: 'flex', gap: {xs: 1, md: 3}, position: 'relative'}}>
            <Box sx={{flex: 1, display: 'flex', flexDirection: 'column'}}>
              {currentSection && (
                <>
                  {isMobile && (
                    <StudyPageSectionNavigation
                      currentIndex={currentSectionIndex}
                      totalSections={sections.length}
                      onPrevious={handlePrevious}
                      onNext={handleNext}
                      isMobile
                    />
                  )}
                  <StudyPageSectionContent
                    section={currentSection}
                    subjectColor={subjectColor}
                    isTouchDevice={isTouchDevice}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                  />
                  {!isMobile && (
                    <StudyPageSectionNavigation
                      currentIndex={currentSectionIndex}
                      totalSections={sections.length}
                      onPrevious={handlePrevious}
                      onNext={handleNext}
                    />
                  )}
                </>
              )}
            </Box>

            {!isMobile && (
              <StudyPageTableOfContents
                items={toc}
                currentIndex={currentSectionIndex}
                onItemClick={handleSectionChangeWithToc}
                subjectColor={subjectColor}
              />
            )}
          </Box>
        ) : (
          <Paper
            sx={{
              p: {xs: 2, md: 3},
              bgcolor: 'white',
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              No study content available
            </Typography>
          </Paper>
        )}
      </StudyPageContent>

      {isMobile && (
        <StudyPageTableOfContentsDrawer
          open={tocOpen}
          onClose={() => setTocOpen(false)}
          items={toc}
          currentIndex={currentSectionIndex}
          onItemClick={handleSectionChangeWithToc}
          subjectColor={subjectColor}
        />
      )}
    </ContentContainer>
  );
}
